import { apiFetch } from "@/api/client";
import { useAuthStore } from "@/store/authStore";

export interface UserSummary {
  username: string;
  enabled: boolean;
  simultaneous_use: number | null;
}

export interface UserListResponse {
  items: UserSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface FileUserSummary {
  username: string;
  simultaneous_use: number | null;
  has_password: boolean;
}

export interface FileUserListResponse {
  items: FileUserSummary[];
  total: number;
  source_path: string;
}

export const listFileBasedUsers = () => apiFetch<FileUserListResponse>("/users/file-based");

export interface ImportPreviewRow {
  username: string;
  action: "create" | "skip_exists" | "invalid";
  reason?: string | null;
}

export interface ImportResult {
  dry_run: boolean;
  created: number;
  skipped: number;
  rows: ImportPreviewRow[];
}

export const listUsers = (search: string, page: number) =>
  apiFetch<UserListResponse>(
    `/users?page=${page}&page_size=25${search ? `&search=${encodeURIComponent(search)}` : ""}`,
  );

export const createUser = (username: string, password: string, simultaneousUse: number) =>
  apiFetch(`/users`, {
    method: "POST",
    body: JSON.stringify({ username, password, simultaneous_use: simultaneousUse }),
  });

export const deleteUser = (username: string) =>
  apiFetch(`/users/${encodeURIComponent(username)}`, { method: "DELETE" });

export const setUserEnabled = (username: string, enabled: boolean) =>
  apiFetch(`/users/${encodeURIComponent(username)}/${enabled ? "enable" : "disable"}`, {
    method: "POST",
  });

export const resetPassword = (username: string, password: string) =>
  apiFetch(`/users/${encodeURIComponent(username)}/password`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });

export const setSimultaneousUse = (username: string, value: number) =>
  apiFetch(`/users/${encodeURIComponent(username)}/simultaneous-use`, {
    method: "PUT",
    body: JSON.stringify({ simultaneous_use: value }),
  });

export async function downloadUsersExport(): Promise<void> {
  // A plain <a href> wouldn't carry the Authorization header, so this fetches
  // with auth and triggers the download from the resulting blob instead.
  const { accessToken } = useAuthStore.getState();
  const res = await fetch("/api/v1/users/export", {
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "radadmin-users.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export async function importUsers(file: File, dryRun: boolean): Promise<ImportResult> {
  const form = new FormData();
  form.append("file", file);
  const { accessToken } = useAuthStore.getState();
  const res = await fetch(`/api/v1/users/import?dry_run=${dryRun}`, {
    method: "POST",
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail ?? res.statusText);
  }
  return res.json();
}
